import { Link } from "react-router-dom";
import { ArrowRight, Radio, Users, Plane, Shield, Settings, FileCheck } from "lucide-react";
import { motion } from "framer-motion";

const capabilities = [
  {
    title: "Mission Control",
    description: "Centralised mission planning, dispatch and monitoring. Coordinate operations from a single interface.",
    icon: Radio,
  },
  {
    title: "Asset Coordination",
    description: "Manage multiple aerial and ground assets. Hand-off between operators and platforms as situations evolve.",
    icon: Users,
  },
  {
    title: "Dispatch Workflow",
    description: "Integrate with existing incident management and dispatch systems. Trigger aerial response based on defined rules.",
    icon: Settings,
  },
  {
    title: "Continuity",
    description: "Maintain situational awareness across coverage gaps. Docking and persistent coverage where operationally justified.",
    icon: Shield,
  },
];

const airframes = [
  {
    name: "Stratus",
    status: "Development",
    description: "Long-endurance fixed-wing configuration for extended coverage missions.",
  },
  {
    name: "Pyreion",
    status: "Development",
    description: "High-performance multirotor for rapid response operations.",
  },
  {
    name: "Vitaris",
    status: "Concept",
    description: "Compact multirotor for confined-space and indoor operations.",
  },
];

export default function HelaisPlatformPage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/platforms" className="text-caption hover:text-foreground transition-colors mb-4 inline-block">
              Platforms & Technology
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center">
                <Radio className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
            <h1 className="text-display text-foreground mb-6">
              Helais
            </h1>
            <p className="text-subtitle max-w-2xl mb-4">
              Mission Orchestration & Aerial Operations Platform
            </p>
            <p className="text-body max-w-2xl">
              Helais is a mission orchestration platform—not just a fleet of proprietary drones. Lead with mission control, asset coordination, dispatch workflow and continuity. RLK airframes are optional configurations, not requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission First */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <h2 className="text-headline text-foreground mb-6">
              Mission Orchestration First
            </h2>
            <p className="text-body mb-4">
              Customers need mission orchestration, not a closed fleet of proprietary drones. Helais integrates aerial assets into existing workflows, coordinates multiple platforms, and maintains clear lines of authority.
            </p>
            <p className="text-body">
              The platform supports qualified third-party UAVs and sensors alongside RLK airframe configurations. Use the equipment that meets your operational and procurement requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Capabilities</p>
            <h2 className="text-headline text-foreground">Platform Functions</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {capabilities.map((cap, index) => (
              <motion.div
                key={index}
                className="card-premium p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center mb-4">
                  <cap.icon className="h-5 w-5 text-muted-foreground" />
                </div>
                <h3 className="text-label text-foreground mb-2">{cap.title}</h3>
                <p className="text-sm text-muted-foreground">{cap.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Airframes */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Airframe Configurations</p>
            <h2 className="text-headline text-foreground">RLK Design Concepts</h2>
            <p className="text-body max-w-2xl mx-auto mt-4">
              RLK airframes are optional programme concepts or supported configurations according to maturity. They are not required for Helais deployment.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {airframes.map((airframe, index) => (
              <motion.div
                key={index}
                className="card-pillar p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-label text-foreground">{airframe.name}</h3>
                  <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
                    {airframe.status}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{airframe.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Helais Sentinel */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center mx-auto mb-6">
              <Shield className="h-6 w-6 text-muted-foreground" />
            </div>
            <h2 className="text-headline text-foreground mb-4">
              Helais Sentinel
            </h2>
            <p className="text-body mb-8">
              Helais Sentinel is a dedicated solution for protective security applications. It combines Helais mission orchestration with existing perimeter systems and operator-controlled aerial observation.
            </p>
            <Link to="/solutions/protective-security" className="btn-secondary">
              Learn About Sentinel
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Dependencies */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-headline text-foreground mb-6 text-center">
              Deployment Dependencies
            </h2>
            <div className="card-pillar p-8">
              <p className="text-body mb-6">
                Helais deployment requires clear understanding of regulatory requirements and operational boundaries:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Aviation authority approvals for the target operational environment</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">BVLOS authorisation where applicable</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Operator training and certification requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Site-specific operational limitations</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground italic">
                Regulatory and BVLOS requirements are deployment dependencies, not solved assumptions. We work with you to understand and address these requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Discuss Mission Integration
            </h2>
            <p className="text-body mb-8">
              Tell us about your operational requirements, existing systems, and deployment constraints. We will scope a pilot that demonstrates integration value.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=helais" className="btn-primary">
                Scope a Pilot
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
              <Link to="/contact?type=integration" className="btn-secondary">
                Discuss Mission Integration
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
